import { createSignal } from "solid-js";
import { createStore, reconcile } from "solid-js/store";

import {
  GetPhoneVerifyCode,
  NewForgetCodeSubmit,
  VerifyCodeNewPassword,
  useLoginByAccountStandard,
} from "@/api/clientApi/login.ts";
import DialogSettingCenter from "@/components/Dialogs/DialogCenter.ts";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import { LocalStorageKey, PHONE_CODE } from "@/constants/constants.ts";
import { CodeTypeEnum, VerifyTypeEnum } from "@/enum/loginRegister.ts";
import { getLangKey, t } from "@/i18n";
import { aesDcryptWithPKC27, aesEncryptWithCommonKey } from "@/lib/aesUtils.ts";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@/lib/localStorage.ts";
import { sdkManager } from "@/lib/sdk/sdkManager.ts";
import {
  isValidCPF,
  isValidEmail,
  isValidPassword,
  isValidPhone,
} from "@/lib/validateUtils";
import { userStore } from "@/stores/userStore.ts";

import { getHomePopups } from "../usePopup.ts";
import { useRouter } from "../useRouter.tsx";
import { useAfterLogin } from "./useAfterLogin.tsx";

const defaultLoginFormData = {
  cpf: "",
  phone: "",
  email: "",
  password: "",
  a_code: PHONE_CODE,
  standard: "",
};

const defaultForgetForm1Data = {
  phone: "",
  email: "",
  a_code: PHONE_CODE,
};

const defaultForgetForm2Data = {
  code: "",
  password: "",
  confirm_password: "",
};
export const [loginFormData, setLoginFormData] = createStore<
  typeof defaultLoginFormData
>(JSON.parse(JSON.stringify(defaultLoginFormData)));

export function useLogin() {
  const { afterLoginSuccess } = useAfterLogin();
  const { back, push } = useRouter();
  // Remember me state
  const [rememberMe, setRememberMe] = createSignal(true);
  const [isLoginLoading, setIsLoginLoading] = createSignal(false);
  // Input type state (email or phone)
  const [inputType, setInputType] = createSignal<"email" | "phone">("email");

  const [forgetInputType, setForgetInputType] = createSignal<"email" | "phone">(
    "email",
  );

  const [forgetStep, setForgetStep] = createSignal<"none" | "step1" | "step2">(
    "none",
  );

  const [forgetForm1Data, setForgetForm1Data] = createStore<
    typeof defaultForgetForm1Data
  >(defaultForgetForm1Data);

  const [forgetForm2Data, setForgetForm2Data] = createStore<
    typeof defaultForgetForm2Data
  >(defaultForgetForm2Data);

  // 错误状态管理
  const [loginFormErrors, setLoginFormErrors] = createStore<
    Partial<Record<keyof typeof defaultLoginFormData, string>>
  >({});

  const [forgetForm1Errors, setForgetForm1Errors] = createStore<
    Partial<Record<keyof typeof defaultForgetForm1Data, string>>
  >({});

  const [forgetForm2Errors, setForgetForm2Errors] = createStore<
    Partial<Record<keyof typeof defaultForgetForm2Data, string>>
  >({});
  // 初始化登录form
  const initLoginFormData = () => {
    setLoginFormData(reconcile(defaultLoginFormData));
  };
  // 初始化登录form错误
  const initLoginFormErrors = () => {
    setLoginFormErrors(reconcile({}));
  };
  // 初始化登录form
  const initLoginForm = () => {
    // initLoginStore();
    if (
      useLoginGetRememberMe() &&
      Object.keys(useLoginGetRememberMe()).length > 0
    ) {
      setLoginFormData({
        password: useLoginGetRememberMe().password,
        a_code: useLoginGetRememberMe().a_code || "",
        standard: useLoginGetRememberMe().account,
      });
      setRememberMe(true);
      // if (loginStore.tabActive === LoginTypeEnum.cpf) {
      //   setLoginFormData({
      //     cpf: useLoginGetRememberMe().username,
      //     password: useLoginGetRememberMe().password,
      //   });
      // } else if (loginStore.tabActive === LoginTypeEnum.email) {
      //   //邮箱
      //   setLoginFormData({
      //     email: useLoginGetRememberMe().username,
      //     password: useLoginGetRememberMe().password,
      //   });
      // } else {
      //   //手机号
      //   setLoginFormData({
      //     phone: useLoginGetRememberMe().username,
      //     password: useLoginGetRememberMe().password,
      //     a_code: useLoginGetRememberMe().a_code,
      //   });
      // }
    }
  };

  const validate_LoginForm = (
    fieldOrData:
      | keyof typeof defaultLoginFormData
      | typeof defaultLoginFormData,
    value?: string | number,
  ): boolean => {
    const errors: Partial<typeof defaultLoginFormData> = {};

    const check = (
      field: keyof typeof defaultLoginFormData,
      val: string | number,
    ) => {
      const str = val.toString();
      if (field === "cpf") {
        if (!str) errors.cpf = t("auth.input_cpf_required");
        else if (!isValidCPF(str)) {
          errors.cpf = t("auth.cpfinvalid");
        } else {
          errors[field] = undefined;
        }
      }

      if (field === "phone") {
        if (!str) errors.phone = t("auth.input_phone_required");
        else if (!isValidPhone(loginFormData.a_code, str)) {
          errors.phone = t("auth.phoneinvalid");
        } else {
          errors[field] = undefined;
        }
      }

      if (field === "email") {
        if (!str) errors.email = t("auth.input_email_required");
        else if (!isValidEmail(str)) {
          errors.email = t("auth.emailinvalid");
        } else {
          errors[field] = undefined;
        }
      }

      if (field === "password") {
        if (!str) errors.password = t("auth.input_password_required");
        else if (!isValidPassword(str)) {
          errors.password = t("auth.password_length_required", { val: 6 });
          sdkManager.tdReportEvent("login_password_validation_failed", {});
        } else {
          errors[field] = undefined;
          sdkManager.tdReportEvent("login_password_validation_success", {});
        }
      }
      if (field === "standard") {
        if (!str) errors.standard = t("auth.input_required");
        else {
          errors[field] = undefined;
        }
      }
    };

    if (typeof fieldOrData === "string" && value !== undefined) {
      check(fieldOrData, value);
    } else {
      let data = {} as Partial<typeof defaultLoginFormData>;
      // if (loginStore.tabActive === LoginTypeEnum.cpf) {
      //   data = {
      //     cpf: loginFormData.cpf,
      //     password: loginFormData.password,
      //   };
      // } else if (loginStore.tabActive === LoginTypeEnum.email) {
      //   data = {
      //     email: loginFormData.email,
      //     password: loginFormData.password,
      //   };
      // } else if (loginStore.tabActive === LoginTypeEnum.phone) {
      //   data = {
      //     a_code: loginFormData.a_code,
      //     phone: loginFormData.phone,
      //     password: loginFormData.password,
      //   };
      // }
      data = {
        standard: loginFormData.standard,
        password: loginFormData.password,
      };
      (Object.keys(data) as (keyof typeof defaultLoginFormData)[]).forEach(
        (key) => {
          check(key, data[key] || "");
        },
      );
    }
    const filteredErrors = Object.fromEntries(
      Object.entries(errors).filter(([_, v]) => v !== undefined),
    );
    setLoginFormErrors(errors);
    return Object.keys(filteredErrors).length === 0;
  };

  const validate_ForgetForm1 = (
    fieldOrData:
      | keyof typeof defaultForgetForm1Data
      | typeof defaultForgetForm1Data,
    value?: string | number,
  ): boolean => {
    const errors: Partial<typeof defaultForgetForm1Data> = {};
    const type = forgetInputType();

    const check = (
      field: keyof typeof defaultForgetForm1Data,
      val: string | number,
    ) => {
      const str = val.toString();

      if (field === "phone" && type === "phone") {
        if (!str) errors.phone = t("auth.input_phone_required");
        else if (!isValidPhone(forgetForm1Data.a_code, str))
          errors.phone = t("auth.phoneinvalid");
        else errors[field] = undefined;
      }

      if (field === "email" && type === "email") {
        if (!str) errors.email = t("auth.input_email_required");
        else if (!isValidEmail(str)) errors.email = t("auth.emailinvalid");
        else errors[field] = undefined;
      }
    };

    if (typeof fieldOrData === "string" && value !== undefined) {
      check(fieldOrData, value);
    } else {
      const data = fieldOrData as typeof defaultForgetForm1Data;
      (Object.keys(data) as (keyof typeof defaultForgetForm1Data)[]).forEach(
        (key) => {
          check(key, data[key]);
        },
      );
    }
    setForgetForm1Errors(errors);
    const filteredErrors = Object.fromEntries(
      Object.entries(errors).filter(([_, v]) => v !== undefined),
    );
    return Object.keys(filteredErrors).length === 0;
  };

  const validate_ForgetForm2 = (
    fieldOrData:
      | keyof typeof defaultForgetForm2Data
      | typeof defaultForgetForm2Data,
    value?: string | number,
  ): boolean => {
    const errors: Partial<typeof defaultForgetForm2Data> = {};

    const check = (
      field: keyof typeof defaultForgetForm2Data,
      val: string | number,
    ) => {
      const str = val.toString();

      if (field === "code") {
        if (!str) errors.code = t("auth.coderequired");
      }

      if (field === "password") {
        if (!str) errors.password = t("auth.input_password_required");
        else if (!isValidPassword(str))
          errors.password = t("auth.password_length_required", { val: 6 });
        else errors[field] = undefined;
      }

      if (field === "confirm_password") {
        if (!str) errors.confirm_password = t("auth.confirm_password_required");
        else if (str !== forgetForm2Data.password)
          errors.confirm_password = t("auth.confirm_password_invalid");
        else errors[field] = undefined;
      }
    };

    if (typeof fieldOrData === "string" && value !== undefined) {
      check(fieldOrData, value);
    } else {
      const data = fieldOrData as typeof defaultForgetForm2Data;
      (Object.keys(data) as (keyof typeof defaultForgetForm2Data)[]).forEach(
        (key) => {
          check(key, data[key]);
        },
      );
    }

    setForgetForm2Errors(errors);
    return Object.keys(errors).length === 0;
  };

  //忘记密码获取验证码
  const getCode = async () => {
    const result = await GetPhoneVerifyCode({
      phone: forgetInputType() === "phone" ? forgetForm1Data.phone : "",
      code_type: CodeTypeEnum.CodeTypeUpdatePwd,
      acode: forgetInputType() === "phone" ? forgetForm1Data.a_code : "",
      lang: getLangKey(),
      verify_type:
        forgetInputType() === "email"
          ? VerifyTypeEnum.VerifyTypeEmail
          : VerifyTypeEnum.VerifyTypePhone,
      email: forgetInputType() === "email" ? forgetForm1Data.email : "",
      retry: 0,
      device: {
        sign: "",
        server_id: "",
        need_sid: 0,
        w: 0,
        h: 0,
        gv: "",
        gr: "",
        UA: "",
        client_type: 0,
        app_type: 0,
        m_app: 0,
      },
    });
    return result;
  };

  // 登陆
  const handleSubmit = async (e: Event) => {
    sdkManager.tdReportEvent("login_submit", {});
    setIsLoginLoading(true);
    setLoginFormData("phone", loginFormData.phone);
    const isValid = validate_LoginForm(loginFormData);
    if (!isValid) {
      setIsLoginLoading(false);
      return;
    }
    // if (loginStore.tabActive === LoginTypeEnum.phone) {
    //   const { phone } = loginFormData;
    //   if (!phone || !isValidPhone(PHONE_CODE, phone)) {
    //     // 这里固定使用巴西国家码
    //     setLoginFormErrors({ phone: t("auth.phoneinvalid") });
    //     setIsLoginLoading(false);
    //     return;
    //   }
    // }

    try {
      const result = await useLoginByAccountStandard({
        account: loginFormData.standard,
        password: loginFormData.password,
        rememberMe: rememberMe(),
        a_code: "",
        loginType: "standard",
      });
      setIsLoginLoading(false);
      afterLoginSuccess();
      back();
      // 重置首页弹窗标记，允许登录后显示弹窗
      DialogSettingCenter.resetHomePopupFlag();
      getHomePopups();
      //如果当前路由是login页或者模态框,则返回上一页
      // if (
      //   location.pathname.endsWith("/login") ||
      //   checkModalPath(location.pathname)
      // ) {
      //   back();
      //   getHomePopups();
      // } else {
      //   // 否则无需回退，直接刷新当前页面
      //   window.location.reload();
      // }

      return result;
    } catch (error) {
      setIsLoginLoading(false);
      return false;
    }
  };

  //忘记密码发送
  const handleForgetSend = async (
    setForgetStep: (step: "none" | "step1" | "step2") => void,
  ) => {
    const isValid = validate_ForgetForm1(forgetForm1Data);
    const { showDialog } = useDialog();
    if (isValid) {
      const result = await getCode();
      if (
        result &&
        typeof result === "object" &&
        "errno" in result &&
        !result.errno
      ) {
        if (forgetInputType() === "phone") {
          setForgetStep("step2");
        } else {
          showDialog({
            type: DialogType.Tips,
            data: {
              image:
                "https://web-res-ccc.afunimg8.com/cdn-cgi/image/format=auto/C02/11748/logo/logo-h.png?20250301611",

              content: `<span>${t("auth.check_email_code", {
                code: forgetForm1Data.email,
              })}</span>`,
            },
          });
          setForgetStep("step1");
        }
      }
    }
  };

  //确认验证码秘密
  const handleForgetConfirm = async (
    setForgetStep: (step: "none" | "step1" | "step2") => void,
  ) => {
    const isValid = validate_ForgetForm2(forgetForm2Data);
    if (isValid) {
      const result = await VerifyCodeNewPassword({
        code: forgetForm2Data.code,
        phone: forgetForm1Data.phone,
        email: "",
        acode: forgetForm1Data.a_code,
      });
      if (result) {
        const result2 = await NewForgetCodeSubmit({
          key: result.data,
          new_pwd: forgetForm2Data.password,
        });
        if (result2) {
          setForgetStep("none");
        }
      }
    }
  };
  // 未登陆时去登陆或注册
  const toLoginOrRegister = () => {
    if (!userStore.isLogin) {
      const lastLoginTime = getLocalStorageItem(
        LocalStorageKey.LastLoginTime,
        0,
      );
      if (lastLoginTime) {
        push("login");
      } else {
        push("register");
      }
      return false;
    } else {
      return true;
    }
  };
  // 获取登录数据
  const useLoginGetRememberMe = () => {
    const rememberMeStorageData = getLocalStorageItem(
      LocalStorageKey.REMEMBER_ME,
      "",
    );
    const str = aesDcryptWithPKC27(rememberMeStorageData as string);
    if (str === "") {
      removeLocalStorageItem(LocalStorageKey.REMEMBER_ME);
    }
    const rememberMeData = JSON.parse(str !== "" ? str : "{}");

    return rememberMeData;
  };
  // 删除登录历史
  const removeLoginHistory = () => {
    initLoginFormData();
    initLoginFormErrors();
    setLocalStorageItem(
      LocalStorageKey.REMEMBER_ME,
      aesEncryptWithCommonKey(
        JSON.stringify({
          rememberMe: useLoginGetRememberMe().rememberMe || false,
        }),
      ),
    );
  };
  return {
    rememberMe,
    setRememberMe,
    handleSubmit,
    inputType,
    setInputType,
    loginFormData,
    setLoginFormData,
    loginFormErrors,
    setLoginFormErrors,
    validate_LoginForm,
    forgetForm1Data,
    setForgetForm1Data,
    forgetForm1Errors,
    setForgetForm1Errors,
    validate_ForgetForm1,
    forgetForm2Data,
    setForgetForm2Data,
    forgetForm2Errors,
    setForgetForm2Errors,
    validate_ForgetForm2,
    forgetStep,
    setForgetStep,
    forgetInputType,
    setForgetInputType,
    handleForgetSend,
    handleForgetConfirm,
    isLoginLoading,
    setIsLoginLoading,
    initLoginFormData,
    toLoginOrRegister,
    useLoginGetRememberMe,
    removeLoginHistory,
    initLoginFormErrors,
    initLoginForm,
  };
}
